<template>
    <div class="section pt-1">
        <div class="ss-filter-card">
            <label class="ss-label mal-text" for="ns_program_filter">പദ്ധതി</label>
            <select
                class="form-select form-select-sm mal-text"
                v-model="program_id"
                id="ns_program_filter"
                style="border-radius:10px;"
                @change="loadReport()"
            >
                <option value="">എല്ലാ പദ്ധതികളും</option>
                <option v-for="p in programs" :key="p.id" :value="p.id">{{ p.title }}</option>
            </select>
        </div>
    </div>

    <div class="section text-center" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <template v-if="!loading">
        <!-- Unit-level summary (always shown) -->
        <div class="section mt-2">
            <div class="ss-stats-grid">
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#1a418c;">{{ reportData.unit_total || 0 }}</div>
                    <div class="ss-stat-label mal-text">ആകെ യൂണിറ്റ്</div>
                </div>
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#12a85a;">{{ (reportData.unit_started || []).length }}</div>
                    <div class="ss-stat-label mal-text">ആരംഭിച്ചത്</div>
                </div>
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#d9534f;">{{ (reportData.unit_not_started || []).length }}</div>
                    <div class="ss-stat-label mal-text">ആരംഭിക്കാത്തത്</div>
                </div>
            </div>
        </div>

        <!-- District-level summary (state view only) -->
        <div class="section mt-2" v-if="user_level === 1">
            <div class="ss-stats-grid ss-stats-grid-2">
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#1a418c;">{{ reportData.district_total || 0 }}</div>
                    <div class="ss-stat-label mal-text">ആകെ ജില്ല</div>
                </div>
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#d9534f;">{{ (reportData.district_not_started || []).length }}</div>
                    <div class="ss-stat-label mal-text">ആരംഭിക്കാത്ത ജില്ല</div>
                </div>
            </div>
        </div>

        <!-- Zone-level summary (state/district view) -->
        <div class="section mt-2" v-if="user_level <= 2">
            <div class="ss-stats-grid ss-stats-grid-2">
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#1a418c;">{{ reportData.zone_total || 0 }}</div>
                    <div class="ss-stat-label mal-text">ആകെ മണ്ഡലം</div>
                </div>
                <div class="ss-stat-card">
                    <div class="ss-stat-value" style="color:#d9534f;">{{ (reportData.zone_not_started || []).length }}</div>
                    <div class="ss-stat-label mal-text">ആരംഭിക്കാത്ത മണ്ഡലം</div>
                </div>
            </div>
        </div>

        <!-- Started / Not Started mode toggle -->
        <div class="section mt-2">
            <div class="ss-toggle-pair">
                <button type="button" class="ss-toggle-btn" :class="{ active: mode === 'not_started' }" @click="mode = 'not_started'">
                    <span class="mal-text">ആരംഭിക്കാത്തത്</span>
                </button>
                <button type="button" class="ss-toggle-btn ss-toggle-btn-green" :class="{ active: mode === 'started' }" @click="mode = 'started'">
                    <span class="mal-text">ആരംഭിച്ചത്</span>
                </button>
            </div>
        </div>

        <!-- Level tabs -->
        <div class="section mt-2" v-if="levelTabs.length > 1">
            <div class="ss-level-tabs" :style="{ gridTemplateColumns: 'repeat(' + levelTabs.length + ',1fr)' }">
                <button
                    v-for="t in levelTabs"
                    :key="t.key"
                    type="button"
                    class="ss-level-tab mal-text"
                    :class="{ active: level === t.key }"
                    @click="level = t.key"
                >
                    {{ t.label }} ({{ levelCount(t.key) }})
                </button>
            </div>
        </div>

        <!-- District list -->
        <div class="section mt-2" v-if="level === 'district'">
            <div v-if="!districtListRows.length" class="ss-empty mal-text">
                {{ mode === 'not_started' ? 'എല്ലാ ജില്ലകളും ആരംഭിച്ചിട്ടുണ്ട്' : 'ഒരു ജില്ലയും ആരംഭിച്ചിട്ടില്ല' }}
            </div>
            <div v-for="(d, idx) in districtListRows" :key="d.id" class="ss-row" :class="mode === 'not_started' ? 'ss-row-red' : 'ss-row-green'">
                <span class="ss-row-idx">{{ idx + 1 }}</span>
                <div class="ss-row-name mal-text">{{ d.name }}</div>
                <span v-if="mode === 'not_started'" class="badge bg-danger mal-text">ആരംഭിച്ചിട്ടില്ല</span>
                <span v-else class="badge bg-success">{{ d.started_units }}/{{ d.total_units }} ({{ d.percent }}%)</span>
            </div>
        </div>

        <!-- Zone list -->
        <div class="section mt-2" v-if="level === 'zone'">
            <div v-if="!zoneGroups.length" class="ss-empty mal-text">
                {{ mode === 'not_started' ? 'എല്ലാ മണ്ഡലങ്ങളും ആരംഭിച്ചിട്ടുണ്ട്' : 'ഒരു മണ്ഡലവും ആരംഭിച്ചിട്ടില്ല' }}
            </div>
            <template v-for="g in zoneGroups" :key="g.key">
                <div v-if="user_level === 1" class="ss-group-header mal-text">{{ g.label }} ({{ g.items.length }})</div>
                <div v-for="(z, idx) in g.items" :key="z.id" class="ss-row" :class="mode === 'not_started' ? 'ss-row-red' : 'ss-row-green'">
                    <span class="ss-row-idx">{{ idx + 1 }}</span>
                    <div class="ss-row-name mal-text">{{ z.name }}</div>
                    <span v-if="mode === 'not_started'" class="badge bg-danger mal-text">ആരംഭിച്ചിട്ടില്ല</span>
                    <span v-else class="badge bg-success">{{ z.started_units }}/{{ z.total_units }} ({{ z.percent }}%)</span>
                </div>
            </template>
        </div>

        <!-- Unit list -->
        <div class="section mt-2" v-if="level === 'unit'">
            <div v-if="!unitGroups.length" class="ss-empty mal-text">
                {{ mode === 'not_started' ? 'എല്ലാ യൂണിറ്റുകളും ആരംഭിച്ചിട്ടുണ്ട്' : 'ഒരു യൂണിറ്റും ആരംഭിച്ചിട്ടില്ല' }}
            </div>
            <template v-for="g in unitGroups" :key="g.key">
                <div v-if="user_level <= 2" class="ss-group-header mal-text">{{ g.label }} ({{ g.items.length }})</div>
                <div v-for="(u, idx) in g.items" :key="u.id" class="ss-row" :class="mode === 'not_started' ? 'ss-row-red' : 'ss-row-green'">
                    <span class="ss-row-idx">{{ idx + 1 }}</span>
                    <div class="ss-row-name mal-text">
                        {{ u.name }}
                        <div class="ss-row-sub" v-if="user_level === 1">{{ u.zone_name }} — {{ u.district_name }}</div>
                        <div class="ss-row-sub" v-else-if="user_level === 2">{{ u.zone_name }}</div>
                    </div>
                    <span v-if="mode === 'not_started'" class="badge bg-danger mal-text">ആരംഭിച്ചിട്ടില്ല</span>
                    <span v-else class="badge bg-success mal-text">ആരംഭിച്ചു</span>
                </div>
            </template>
        </div>
    </template>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
</template>

<script>
import UserService from "@/services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "SnehaSparshamNotStartedReport",
    components: { AlertModel },
    data() {
        return {
            loading: false,
            user_level: 1,
            program_id: "",
            programs: [],
            reportData: {},
            mode: "not_started",
            level: "unit",
            alert_type: "",
            alert_message: "",
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        levelTabs() {
            const tabs = [];
            if (this.user_level === 1) tabs.push({ key: "district", label: "ജില്ല" });
            if (this.user_level <= 2) tabs.push({ key: "zone", label: "മണ്ഡലം" });
            tabs.push({ key: "unit", label: "യൂണിറ്റ്" });
            return tabs;
        },
        // Backend already returns these sorted Kerala district-wise (Thiruvananthapuram →
        // Kasargod), so these just filter by mode without re-sorting — a client-side sort would
        // undo that geographic order.
        districtListRows() {
            return this.mode === "not_started"
                ? (this.reportData.district_not_started || [])
                : (this.reportData.district_percent || []).filter((d) => d.started_units > 0);
        },
        zoneListRows() {
            return this.mode === "not_started"
                ? (this.reportData.zone_not_started || [])
                : (this.reportData.zone_percent || []).filter((z) => z.started_units > 0);
        },
        unitListRows() {
            return this.mode === "not_started" ? (this.reportData.unit_not_started || []) : (this.reportData.unit_started || []);
        },
        zoneGroups() {
            return this._groupBy(this.zoneListRows, "district_id", "district_name");
        },
        unitGroups() {
            if (this.user_level === 1) return this._groupBy(this.unitListRows, "district_id", "district_name");
            if (this.user_level === 2) return this._groupBy(this.unitListRows, "zone_id", "zone_name");
            return [{ key: "all", label: "", items: this.unitListRows }];
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace("/");
            return;
        }
        this.user_level = parseInt(this.$route.params.sub_level, 10);
        this.level = this.user_level === 1 ? "district" : this.user_level === 2 ? "zone" : "unit";
        if (this.$route.query.program_id) {
            this.program_id = this.$route.query.program_id;
        }
        this.$root.updateParent("ആരംഭിച്ചത് / ആരംഭിക്കാത്തത്");
        this.loadReport();
    },
    methods: {
        // Uses a Map (not a plain object) to preserve first-seen order — the rows arrive already
        // sorted Kerala district-wise, but a plain object with numeric-looking keys (district_id)
        // would silently re-sort them numerically on enumeration and lose that order.
        _groupBy(rows, keyField, labelField) {
            const map = new Map();
            rows.forEach((row) => {
                const key = row[keyField] || "none";
                if (!map.has(key)) map.set(key, { key, label: row[labelField] || "—", items: [] });
                map.get(key).items.push(row);
            });
            return Array.from(map.values());
        },
        levelCount(key) {
            if (key === "district") return this.districtListRows.length;
            if (key === "zone") return this.zoneListRows.length;
            return this.unitListRows.length;
        },
        loadReport() {
            this.loading = true;
            const data = {
                user_level: this.user_level,
                user_level_id: parseInt(this.$route.params.id, 10),
                program_id: this.program_id || null,
            };
            UserService.authPostRequest("get-sneha-sparsham-not-started-report", data).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === "success") {
                        this.reportData = response.data.data;
                        this.programs = response.data.data.programs || [];
                    } else {
                        this.alert_type = "Warning";
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                    }
                },
                () => {
                    this.loading = false;
                }
            );
        },
    },
};
</script>

<style scoped>
.ss-filter-card {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.ss-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 4px;
    display: block;
}

.ss-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}
.ss-stats-grid-2 {
    grid-template-columns: repeat(2, 1fr);
}
.ss-stat-card {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 10px 6px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.ss-stat-value { font-size: 18px; font-weight: 800; line-height: 1; }
.ss-stat-label { font-size: 10.5px; color: #888; font-weight: 600; margin-top: 4px; }

.ss-toggle-pair {
    display: flex;
    gap: 6px;
}
.ss-toggle-btn {
    flex: 1;
    padding: 9px;
    border-radius: 10px;
    border: none;
    font-weight: 700;
    font-size: 12.5px;
    background: #f0f0f0;
    color: #666;
}
.ss-toggle-btn.active { background: #d9534f; color: #fff; }
.ss-toggle-btn-green.active { background: #12a85a; color: #fff; }

.ss-level-tabs {
    display: grid;
    gap: 6px;
}
.ss-level-tab {
    padding: 8px;
    border-radius: 10px;
    border: 1px solid #eef0f5;
    background: #fff;
    font-weight: 700;
    font-size: 12px;
    color: #64748b;
}
.ss-level-tab.active { background: #1a418c; color: #fff; border-color: #1a418c; }

.ss-empty {
    text-align: center;
    color: #888;
    font-size: 13px;
    padding: 20px 0;
}

.ss-group-header {
    padding: 8px 4px 4px;
    font-size: 12px;
    font-weight: 800;
    color: #1a418c;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    border-bottom: 2px solid #e8eef8;
    margin: 10px 0 6px;
}

.ss-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 6px;
    border: 1px solid #eee;
}
.ss-row-red { border-left: 3px solid #d9534f; }
.ss-row-green { border-left: 3px solid #12a85a; }
.ss-row-idx { color: #ddd; font-size: 12px; width: 18px; flex-shrink: 0; text-align: right; }
.ss-row-name { flex: 1; min-width: 0; font-size: 13px; font-weight: 700; color: #27173e; }
.ss-row-sub { font-size: 10.5px; color: #999; font-weight: 500; margin-top: 2px; }
</style>
